import React, { ReactElement, useState } from 'react';
import { Radio, RadioGroup } from '../../../../ps-aspect-design';
import { Input } from 'ps-design';
import Select from 'ps-components/ReactSelect/ReactSelect';
import { Link } from 'react-router-dom';
import { MarketElements, RuleBasedPricingRule } from '../GlobalJobMatchingService';
import { DataCutPriority, JobsSelection, Methodology, PricingRule, RuleStep } from '../types/rules';
import { FormGroup } from 'ps-design';
import './RuleStepCard.scss';
import { numberWithCommas } from 'ps-components/lib/formatters';
import FieldFilters from 'ps-components/FieldFilters/FieldFilters';
import { MatchStrategy, PricingVersion } from '../../../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/versions';

type SelectOptions = ({ value: string; label: string } | undefined)[];
interface RuleStepComponentProps {
  pricingVersion: PricingVersion;
  rule: PricingRule;
  currentRuleStep: RuleStep;
  defaultGroup: { id: string; name: string };
  groups: { id: string; name: string }[];
  jobPricingRules: RuleBasedPricingRule[];
  marketElements: MarketElements;
  handleRuleUpdate: (arg0: PricingRule) => void;
  filters: Array<any>;
  areFiltersLoading: boolean;
  areFiltersLoaded: boolean;
}

const MIN_INPUT_NUMBER = 0;
const MAX_INPUT_NUMBER = 500000;
const MIN_SIZE_INPUT_ERROR = `Please input a number greater than ${MIN_INPUT_NUMBER}`;
const MAX_SIZE_INPUT_ERROR = `Please input a number less than ${numberWithCommas(MAX_INPUT_NUMBER)}`;

const RuleStepCard = ({
  pricingVersion,
  rule,
  currentRuleStep,
  defaultGroup,
  groups,
  jobPricingRules,
  marketElements,
  handleRuleUpdate,
  filters,
  areFiltersLoaded,
  areFiltersLoading
}: RuleStepComponentProps): ReactElement => {
  const [incumbentsError, setIncumbentsError] = useState('');
  const [companiesError, setCompaniesError] = useState('');
  const [selectedDataCutPriority, setSelectedDataCutPriority] = useState(rule.dataCutPriority || DataCutPriority.DEFAULT);
  const [selectedDataCutOption, setSelectedDataCutOption] = useState<string | null>(rule.dataCutPrioritySelection || null);

  const getMarketElementOptions = (): SelectOptions => {
    return (
      marketElements?.payElements.map(elem => {
        return { label: elem.field_title, value: elem.source_field };
      }) || []
    );
  };

  const getSelectedMarketElementOptions = (rule: PricingRule): SelectOptions => {
    if (rule.marketDataElements) {
      return rule.marketDataElements
        .filter(elem => {
          return !!marketElements.payElements.find(payElem => payElem.source_field === elem);
        })
        .map(elem => {
          const foundElem = marketElements.payElements.find(payElem => payElem.source_field === elem);
          if (!foundElem) return;
          return { value: foundElem.source_field, label: foundElem.field_title };
        });
    }
    return [];
  };

  const onChange = (filtersToSave: Array<any>) => {
    const newDetails = {
      ...rule,
      jobsFilter: filtersToSave
    };
    handleRuleUpdate(newDetails);
  }

  const renderJobsFilter = () => {
    return (
      <FieldFilters
        loading={areFiltersLoading}
        loaded={areFiltersLoaded}
        elements={filters}
        currentFilters={rule.jobsFilter}
        noFilterPlaceholder="No Job Filter Elements Found"
        onChange={onChange}
        hideActions={true}
      />
    );
  };

  const getDataCutPriorityOptions = () => {
    const dataCutPriorityOptions = [{ label: "Site Level", value: DataCutPriority.DEFAULT }];
    if (groups.length) {
      dataCutPriorityOptions.push({ label: "Group Level", value: DataCutPriority.GROUP_SPECIFIC });
    }
    if (jobPricingRules.length) {
      dataCutPriorityOptions.push({ label: "Rules Based Pricing", value: DataCutPriority.RULES_BASED_PRICING });
    }
    return dataCutPriorityOptions;
  }

  const getDataCutPrioritySelectedOption = () => {
    return getDataCutPriorityOptions().find(option => option.value === selectedDataCutPriority);
  }

  const getDataCutOptions = () => {
    if (selectedDataCutPriority === DataCutPriority.GROUP_SPECIFIC) {
      return groups
        .map((group: any) => {
          return { label: group.name, value: group.id };
        });
    } else if (selectedDataCutPriority === DataCutPriority.RULES_BASED_PRICING) {
      return jobPricingRules?.map((rule: any) => {
        return { label: rule.name, value: rule.id };
      })
    }
    return [{ label: defaultGroup.name, value: defaultGroup.id }];
  }

  const getDataCutSelectedOption = () => {
    if (selectedDataCutPriority === DataCutPriority.DEFAULT) {
      return { label: defaultGroup.name, value: defaultGroup.id };
    }
    if (selectedDataCutOption === null) {
      return null;
    }
    return getDataCutOptions().find(option => parseInt(option.value) === parseInt(selectedDataCutOption));
  }

  if (currentRuleStep === RuleStep.SELECT_JOBS) {
    return (
      <div className="rule-step__card">
        <div className="card-content__header">What Jobs do you want to include in this rule?</div>
        <RadioGroup name={`rule-group-${rule.id}`}>
          <Radio
            label="All Jobs"
            value={JobsSelection.ALL_JOBS}
            checked={rule.jobsSelection === JobsSelection.ALL_JOBS}
            onChange={(): void => {
              const newDetails = {
                ...rule,
                jobsSelection: JobsSelection.ALL_JOBS
              };
              handleRuleUpdate(newDetails);
            }}
          />
          <Radio
            label="Select Jobs"
            value={JobsSelection.SELECT_JOBS}
            checked={rule.jobsSelection === JobsSelection.SELECT_JOBS}
            onChange={(): void => {
              const newDetails = {
                ...rule,
                jobsSelection: JobsSelection.SELECT_JOBS
              };
              handleRuleUpdate(newDetails);
            }}
          />
        </RadioGroup>
        {rule.jobsSelection === JobsSelection.SELECT_JOBS && renderJobsFilter()}
      </div>
    );
  } else if (currentRuleStep === RuleStep.DATA_CUT_PRIORITIES) {
    const dataCutPriorityIsNotGroup = selectedDataCutPriority === DataCutPriority.DEFAULT || selectedDataCutPriority === DataCutPriority.RULES_BASED_PRICING;
    const dataCutPrioritiesLinkPath = selectedDataCutPriority === DataCutPriority.DEFAULT ? '/iframe/merit/merit-admin/survey_lib' : `/market-analysis/rules-based-pricing/rules/${selectedDataCutOption}`;
    const dataCutPrioritiesLinkText = selectedDataCutPriority === DataCutPriority.DEFAULT ? "Data Cut Priorities" : "Rules Based Pricing";
    return (
      <div className="rule-step__card two-column__card">
        <div className="rule-step__card-column">
          <div className="card-content__header">Select Data Cut Priorities</div>
          <div className="select-data-cut-priority__group">
            Use
            <Select
              className="data-cut-priorities__dropdown"
              value={getDataCutPrioritySelectedOption()}
              options={getDataCutPriorityOptions()}
              onChange={(selected: { label: string; value: DataCutPriority }): void => {
                setSelectedDataCutPriority(selected.value);
                let newSelectedDataCut;
                if (selected.value === DataCutPriority.GROUP_SPECIFIC) {
                  newSelectedDataCut = groups[0].id;
                } else if (selected.value === DataCutPriority.RULES_BASED_PRICING) {
                  newSelectedDataCut = jobPricingRules[0].id;
                } else {
                  newSelectedDataCut = defaultGroup.id;
                }
                setSelectedDataCutOption(newSelectedDataCut);

                const newDetails = {
                  ...rule,
                  dataCutPriority: selected.value,
                  dataCutPrioritySelection: newSelectedDataCut
                };
                handleRuleUpdate(newDetails);
              }}
            />
              {selectedDataCutPriority &&
                <Select
                  className="data-cut-priorities__dropdown"
                  value={getDataCutSelectedOption()}
                  options={getDataCutOptions()}
                  onChange={(selected: { label: string; value: string }): void => {
                    const newDetails = {
                      ...rule
                    };
                    setSelectedDataCutOption(selected.value);
                    if (selected.value) newDetails.dataCutPrioritySelection = selected.value;
                    if (selectedDataCutPriority) newDetails.dataCutPriority = selectedDataCutPriority;
                    handleRuleUpdate(newDetails);
                  }}
                />
              }

            Data Cut Priorities for this group of jobs
          </div>
        </div>
         {dataCutPriorityIsNotGroup && (
            <Link
              className="data-cut-priorities__link rule-step__card-column"
              target='_blank'
              to={{ pathname: dataCutPrioritiesLinkPath }}
            >
             {dataCutPrioritiesLinkText}
            </Link>
          )}
      </div>
    );
  } else if (currentRuleStep === RuleStep.METHODOLOGY) {
    return (
      <div className="rule-step__card">
        <div className="card-content__header">Methodology</div>
        {pricingVersion.matchStrategy === MatchStrategy.CURRENT ?
          <div className="subtext">
            For each survey, the match with the highest data cut priority will be used
          </div> :
          <>
            <div className="subheader">Match Priorities vs. Data Cut Priorities</div>
            <RadioGroup name={`methodology-group-${rule.id}`} className="methodology__group">
              <Radio
                label="Prioritize match priorities over data cut priorities"
                value={Methodology.MATCH_PRIORITY}
                checked={rule.methodology === Methodology.MATCH_PRIORITY}
                onChange={(): void => {
                  const newRuleDetails = {
                    ...rule,
                    methodology: Methodology.MATCH_PRIORITY
                  };
                  handleRuleUpdate(newRuleDetails);
                }}
              />
              <div className="subtext">
                For each survey, the match with the highest match priority will be used, followed by data cut priority
              </div>
              <Radio
                label="Prioritize data cut priorities over match priorities"
                value={Methodology.DATA_CUT_PRIORITY}
                checked={rule.methodology === Methodology.DATA_CUT_PRIORITY}
                onChange={(): void => {
                  const newRuleDetails = {
                    ...rule,
                    methodology: Methodology.DATA_CUT_PRIORITY
                  };
                  handleRuleUpdate(newRuleDetails);
                }}
              />
              <div className="subtext">
                For each survey, the match with the highest data cut priority will be used, followed by the match priority
              </div>
            </RadioGroup>
          </>
        }
        <div className="subheader">Participation size</div>
        <div className="participation-size-input-group">
          Minimum
          <FormGroup error={incumbentsError}>
            <Input
              type="number"
              className="incumbents__input"
              placeholder="e.g. 500"
              defaultValue={rule.incumbentsMinimum}
              min={MIN_INPUT_NUMBER}
              max={MAX_INPUT_NUMBER}
              onChange={(id: any, value: string): void => {
                if (parseInt(value)) {
                  if (parseInt(value) < 0) {
                    setIncumbentsError(MIN_SIZE_INPUT_ERROR);
                  } else if (parseInt(value) > MAX_INPUT_NUMBER) {
                    setIncumbentsError(MAX_SIZE_INPUT_ERROR);
                  } else {
                    setIncumbentsError('');
                  }
                } else if (!value) {
                  setIncumbentsError('');
                }
              }}
              onBlur={(e: any): void => {
                let value = e.target.value;
                if (value === '') {
                  value = null;
                }
                if (
                  value === null ||
                  (parseInt(value) && parseInt(value) > MIN_INPUT_NUMBER && parseInt(value) < MAX_INPUT_NUMBER)
                ) {
                  const newRuleDetails = {
                    ...rule,
                    incumbentsMinimum: value
                  };
                  handleRuleUpdate(newRuleDetails);
                }
              }}
            />
          </FormGroup>
          Incumbents
        </div>
        <div className="participation-size-input-group">
          Minimum
          <FormGroup error={companiesError}>
            <Input
              type="number"
              className="companies__input"
              placeholder="e.g. 500"
              defaultValue={rule.companiesMinimum}
              min={MIN_INPUT_NUMBER}
              max={MAX_INPUT_NUMBER}
              onChange={(id: any, value: string): void => {
                if (parseInt(value)) {
                  if (parseInt(value) < 0) {
                    setCompaniesError(MIN_SIZE_INPUT_ERROR);
                  } else if (parseInt(value) > MAX_INPUT_NUMBER) {
                    setCompaniesError(MAX_SIZE_INPUT_ERROR);
                  } else {
                    setCompaniesError('');
                  }
                } else if (!value) {
                  setCompaniesError('');
                }
              }}
              onBlur={(e: any): void => {
                let value = e.target.value;
                if (value === '') {
                  value = null;
                }
                if (
                  value === null ||
                  (parseInt(value) && parseInt(value) > MIN_INPUT_NUMBER && parseInt(value) < MAX_INPUT_NUMBER)
                ) {
                  const newRuleDetails = {
                    ...rule,
                    companiesMinimum: value
                  };
                  handleRuleUpdate(newRuleDetails);
                }
              }}
            />
          </FormGroup>
          Companies
        </div>
        <div className="subheader">Required market data elements for pricing results</div>
        <Select
          className="market-data-element__select"
          isClearable={true}
          isMulti
          onChange={(options: Array<any>) => {
            const selectedMarketElements = options.map(opt => opt.value);
            const newDetails = {
              ...rule,
              marketDataElements: selectedMarketElements
            };
            handleRuleUpdate(newDetails);
          }}
          noOptionsMessage={() => 'No results found'}
          options={getMarketElementOptions()}
          placeholder="Type to search"
          value={getSelectedMarketElementOptions(rule)}
        />
      </div>
    );
  }
  return <></>;
};

export default RuleStepCard;
